import React, { useEffect, useState, useMemo } from "react";
import { ISalary, SalaryType } from "../../types/salary.type";
import { api } from "../../api/api";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  message,
  DatePicker,
} from "antd";
import { DownOutlined, HistoryOutlined } from "@ant-design/icons";
import { Cashbox } from "../../types/cashbox.type";
import useGetRequest from "../../hooks/useGetRequest";
import { useNavigate } from "react-router-dom";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { Currency } from "../../types/currency.type";
import { Machine } from "../../types/machine.type";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import WorkerSalaryHistory from "../../components/WorkerSalaryHistory/workerSalaryHistory.component";
import dayjs from "dayjs";
import { ExpenseType } from "../../types/transaction.type";
import { canEdit } from "../../utils/roleUtils";

const { Title } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

function toDDMMYY(dateStr: string) {
  if (!dateStr) return "";
  const [yyyy, mm, dd] = dateStr.split("-");
  return `${dd}.${mm}.${yyyy.slice(2)}`;
}

// --- ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ДЛЯ ИТОГОВ ПО ДЕТАЛЯМ ---
function getDetailTotals(salaryReports: readonly any[], detailColumnsGrouped: any[], reportWorker: any) {
  return detailColumnsGrouped.map((detail: any) => {
    let totalQty = 0;
    let totalSum = 0;
    salaryReports.forEach((report) => {
      const d = report.details[detail.children[0].key.split('-')[1]];
      if (d) {
        const qty = (d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0);
        const price = d.price || (reportWorker?.pricings?.[detail.children[0].key.split('-')[1]]?.perUnit || 0);
        totalQty += qty;
        totalSum += qty * price;
      }
    });
    return { qty: totalQty, sum: totalSum };
  });
}

// --- ВСПОМОГАТЕЛЬНАЯ ФУНКЦИЯ ДЛЯ ФОРМАТА ПЕРИОДА ---
function formatPeriod(range: [string, string] | null) {
  if (!range || !range[0] || !range[1]) return '';
  const [start, end] = range;
  const toHuman = (d: string) => {
    if (!d) return '';
    const [yyyy, mm, dd] = d.split('-');
    return `${dd}.${mm}.${yyyy}`;
  };
  return `Период: ${toHuman(start)} — ${toHuman(end)}`;
}

export default function Salary() {
  const { authState } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const navigate = useNavigate();
  const [salaries, setSalaries] = useState<ISalary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [hasDebt, setHasDebt] = useState<boolean | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPerDayOpen, setIsPerDayOpen] = useState(false);
  const [isTransactionOpen, setIsTransactionOpen] = useState(false);
  const [isEditDebtPaidOpen, setIsEditDebtPaidOpen] = useState(false);
  const [isSalaryHistoryOpen, setIsSalaryHistoryOpen] = useState(false);
  const [isBonusOpen, setIsBonusOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState<ISalary | null>(null);
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [salaryType, setSalaryType] = useState<SalaryType | undefined>();
  const [form] = Form.useForm();
  const [perDayForm] = Form.useForm();
  const [transactionForm] = Form.useForm();
  const [editDebtPaidForm] = Form.useForm();
  const [bonusForm] = Form.useForm();
  const { data: cashboxesList, loading: cashboxesListLoading } =
    useGetRequest(`/cashbox/list`);
  const { data: machineList, loading: machineListLoading } =
    useGetRequest("/machine/list");
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportWorker, setReportWorker] = useState<ISalary | null>(null);
  const [reportDateRange, setReportDateRange] = useState<[string, string] | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [salaryReports, setSalaryReports] = useState<any[]>([]);
  const [salaryTransactions, setSalaryTransactions] = useState<any[]>([]);
  const [yukPriceUSD, setYukPriceUSD] = useState<number>(0);
  const [yukPriceTJS, setYukPriceTJS] = useState<number>(0);

  // Move useMemo hooks to top level
  const salaryCurrency = useMemo(() => (reportWorker as any)?.salaryCurrency || (reportWorker as any)?.currency || 'USD', [reportWorker]);
  const yukPrice = useMemo(() => salaryCurrency === 'USD' ? yukPriceUSD : yukPriceTJS, [salaryCurrency, yukPriceUSD, yukPriceTJS]);

  // --- ВСПОМОГАТЕЛЬНЫЕ КОЛОНКИ ДЛЯ ДЕТАЛЕЙ ---
  const detailColumnsGrouped = React.useMemo(() => {
    if (!Array.isArray(salaryReports) || !salaryReports.length || !salaryReports[0].details) return [];
    // Показываем только те детали, у которых хотя бы в одном отчете quantity > 0
    const shownDetails = (salaryReports[0].details as any[])
      .map((detail: any, idx: number) => {
        const hasQty = salaryReports.some((r: any) => {
          const d = r.details[idx];
          return d && ((d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0)) !== 0;
        });
        return hasQty ? { ...detail, idx } : null;
      })
      .filter(Boolean);
    // Для каждой детали — группа из 3 колонок: Кол-во, Цена, Сумма
    return shownDetails.map((detail: any) => ({
      title: `${detail.forName}`,
      children: [
        {
          title: 'Кол-во',
          dataIndex: ['details', detail.idx, 'quantity'],
          key: `qty-${detail.idx}`,
          width: 90,
          render: (_: any, record: any) => {
            const d = record.details[detail.idx];
            return d ? (d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0) : 0;
          },
        },
        {
          title: 'Цена',
          dataIndex: ['details', detail.idx, 'price'],
          key: `price-${detail.idx}`,
          width: 90,
          render: (_: any, record: any) => {
            const d = record.details[detail.idx];
            return d ? d.price || (reportWorker?.pricings?.[detail.idx]?.perUnit || 0) : 0;
          },
        },
        {
          title: 'Сумма',
          dataIndex: ['details', detail.idx, 'sum'],
          key: `sum-${detail.idx}`,
          width: 90,
          render: (_: any, record: any) => {
            const d = record.details[detail.idx];
            const price = d ? d.price || (reportWorker?.pricings?.[detail.idx]?.perUnit || 0) : 0;
            const qty = d ? (d.dayQuantity || 0) + (d.nightQuantity || 0) - (d.wasteQuantity || 0) : 0;
            return (qty * price).toLocaleString(undefined, { maximumFractionDigits: 2 });
          },
        },
      ],
    }));
  }, [salaryReports, reportWorker]);

  const reportColumns = React.useMemo(() => [
    { 
      title: '№', 
      key: 'index', 
      fixed: 'left' as 'left', 
      width: 60,
      render: (_: any, __: any, index: number) => index + 1
    },
    { title: 'Дата', dataIndex: 'date', key: 'date', fixed: 'left' as 'left', width: 100 },
    ...detailColumnsGrouped,
    {
      title: 'ЮК',
      children: [
        { title: 'кол', dataIndex: 'yuk', key: 'yuk', width: 80 },
        { title: 'сумма', key: 'yukSum', width: 120, render: (_: any, record: any) => ((record.yuk || 0) * yukPrice).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + salaryCurrency },
      ],
    },
    {
      title: 'Сумма за день',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 120,
      render: (value: any) => value ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + salaryCurrency : '0 ' + salaryCurrency,
    },
  ], [detailColumnsGrouped, yukPrice, salaryCurrency]);

  const handleExport = () => {
    downloadFile("/export/salaries", "salaries_data.xlsx");
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleEditPerDay = async (values: { perDay: number }) => {
    setConfirmLoading(true);

    try {
      await api.put(`/salary/per-day/${selectedSalary?.workerId}`, values);
      message.success("Цены успешно обновлены.");
      fetchSalaries();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating report", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsPerDayOpen(false);
    }
  };

  const handleSubmitForm = (values: any) => {
    setConfirmLoading(true);

    const updatedPricings = selectedSalary?.pricings.map((pricing) => {
      const formData = values[pricing._id];

      const updatedPricing = {
        forName: formData.forName || pricing.forName,
        ...(selectedSalary?.salaryType === SalaryType.PERMONTH
          ? { perMonth: formData.perMonth }
          : { perUnit: formData.perUnit }),
      };

      return updatedPricing;
    });

    api
      .put(`/salary/${selectedSalary?.workerId}`, { pricings: updatedPricings })
      .then(() => {
        message.success("Цены успешно обновлены.");
        setIsModalOpen(false);
        fetchSalaries();
        setConfirmLoading(false);
      })
      .catch((error) => {
        console.error("Error updating prices:", error);
        message.error(error.response.data.error);
        setConfirmLoading(false);
      });
  };

  const handleTransactionCreate = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(`/salary/${selectedSalary?._id}/transaction`, data);

      message.success("Оплата успешно создана.");

      setIsTransactionOpen(false);
      setConfirmLoading(false);
      fetchSalaries();
      transactionForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving cashbox:", error);
      message.error(error.response.data.error);
    }
  };

  const handleAddPricing = () => {
    const newPricing: ISalary["pricings"][0] = {
      _id: Date.now().toString(),
      forName: "",
      perUnit:
        selectedSalary?.salaryType === SalaryType.PERUNIT ? 0 : undefined,
      perMonth:
        selectedSalary?.salaryType === SalaryType.PERMONTH ? 0 : undefined,
    };
    const updatedPricings = selectedSalary?.pricings.concat(newPricing);

    setSelectedSalary({
      ...selectedSalary!,
      pricings: updatedPricings!,
    });

    form.setFieldsValue({
      [newPricing._id]: {
        forName: newPricing.forName,
        perUnit: newPricing.perUnit,
        perMonth: newPricing.perMonth,
      },
    });
  };

  const handleModalOpen = (salary: ISalary) => {
    if (salary.salaryType === SalaryType.PERDAY) {
      setIsPerDayOpen(true);
      setSelectedSalary(salary);
      perDayForm.setFieldsValue({ perDay: salary.perDay });
    } else {
      setIsModalOpen(true);
      setSelectedSalary(salary);

      const initialValues = salary.pricings.reduce(
        (acc: { [key: string]: any }, pricing) => {
          acc[pricing._id] = {
            forName: pricing.forName,
            perMonth: pricing.perMonth,
            perUnit: pricing.perUnit,
          };
          return acc;
        },
        {}
      );

      form.setFieldsValue(initialValues);
    }
  };

  const handleTransactionClick = (salary: ISalary) => {
    setIsTransactionOpen(true);
    setSelectedSalary(salary);
  };

  const handleEditDebtPaidClick = (salary: ISalary) => {
    setIsEditDebtPaidOpen(true);
    setSelectedSalary(salary);
    editDebtPaidForm.setFieldsValue({
      debt: salary.debt,
      paid: salary.paid,
    });
  };

  const handleSalaryHistoryClick = (salary: ISalary) => {
    setIsSalaryHistoryOpen(true);
    setSelectedSalary(salary);
  };

  const handleBonusClick = (salary: ISalary) => {
    setIsBonusOpen(true);
    setSelectedSalary(salary);
  };

  const handleBonusCreate = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(`/salary/bonus/${selectedSalary?.workerId}`, data);

      message.success("Премия успешно создана.");

      setIsBonusOpen(false);
      setConfirmLoading(false);
      fetchSalaries();
      bonusForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating bonus:", error);
      message.error(error.response.data.error);
    }
  };

  const handleEditDebtPaid = async (values: { debt: number; paid: number }) => {
    setConfirmLoading(true);
    try {
      await api.put(`/salary/${selectedSalary?.workerId}/debt-paid`, values);
      message.success("Долг и оплата успешно обновлены.");
      fetchSalaries();
      setIsEditDebtPaidOpen(false);
    } catch (error: any) {
      console.error("Error updating debt and paid:", error);
      message.error(error.response?.data?.error || "Ошибка при обновлении");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeletePricing = (pricingId: string) => {
    const updatedPricings = selectedSalary?.pricings.filter(
      (pricing) => pricing._id !== pricingId
    );

    setSelectedSalary({
      ...selectedSalary!,
      pricings: updatedPricings!,
    });
  };

  const handleOpenReportModal = async (salary: ISalary) => {
    setReportWorker(salary);
    setIsReportModalOpen(true);
    setReportDateRange(["", ""]);
    setSalaryReports([]);
    setSalaryTransactions([]);
    // Получаем yukPrice при открытии модалки
    try {
      const res = await api.get("/settings");
      setYukPriceUSD(res.data.settings?.yukPriceUSD || 0);
      setYukPriceTJS(res.data.settings?.yukPriceTJS || 0);
    } catch (e) {
      setYukPriceUSD(0);
      setYukPriceTJS(0);
    }
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
    setReportWorker(null);
    setSalaryReports([]);
    setSalaryTransactions([]);
  };

  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const response = await api.get("/salary", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          hasDebt: hasDebt ? true : undefined,
          currency,
          salaryType,
        },
      });

      setSalaries(response.data.salaries);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching salaries", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalaries();
  }, [page, searchTerm, pageSize, hasDebt, currency, salaryType]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  useEffect(() => {
    if (exchangeRate) {
      bonusForm.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, bonusForm]);

  const columns = [
    {
      title: "Имя",
      dataIndex: "workerName",
      key: "workerName",
      fixed: "left" as "left",
    },
    {
      title: "Фамилия",
      dataIndex: "workerSurname",
      key: "workerSurname",
    },
    {
      title: "Тип зарплаты",
      dataIndex: "salaryType",
      key: "salaryType",
    },
    {
      title: "Валюта зарплаты",
      dataIndex: "salaryCurrency",
      key: "salaryCurrency",
    },
    {
      title: "Заплатили",
      dataIndex: "paid",
      key: "paid",
    },
    {
      title: "Надо заплатить",
      dataIndex: "debt",
      key: "debt",
    },
    {
      title: "Цены",
      key: "pricings",
      render: (_: any, record: ISalary) => {
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              maxWidth: "1400px",
            }}
          >
            {record.pricings.map((pricing, index) => (
              <p key={index}>
                {index + 1}. {pricing.forName}, Ставка:{pricing.perUnit}
                {pricing.perMonth}
                {pricing.quantity
                  ? `, Кол-во выполнено: ${pricing.quantity}`
                  : null}
              </p>
            ))}
          </div>
        );
      },
    },
    {
      title: "За день",
      dataIndex: "perDay",
      key: "perDay",
    },
    {
      title: "Начало",
      dataIndex: "periodStart",
      key: "periodStart",
    },
    {
      title: "Обновлено",
      dataIndex: "lastUpdate",
      key: "lastUpdate",
    },
    {
      title: "",
      key: "actions",
      fixed: "right" as "right",
      render: (_: any, record: ISalary) => {
        const menu = (
          <Menu>
            {/* Действия редактирования */}
            {(canEdit(authState.user?.role) ||
              authState.user?.role === UserRole.Accountant) && (
                <Menu.Item onClick={() => handleModalOpen(record)}>
                  Изменить цену
                </Menu.Item>
              )}
              
            {canEdit(authState.user?.role) && (
              <Menu.Item onClick={() => handleEditDebtPaidClick(record)}>
                Изменить долг и оплату
              </Menu.Item>
            )}
            
            {(canEdit(authState.user?.role) ||
              authState.user?.role === UserRole.Cashier ||
              authState.user?.role === UserRole.Accountant) && (
                <>
                  <Menu.Item onClick={() => handleTransactionClick(record)}>
                    Добавить оплату
                  </Menu.Item>
                  <Menu.Item onClick={() => handleBonusClick(record)}>
                    Добавить премию
                  </Menu.Item>
                </>
              )}
              
            {/* Действия просмотра (GET запросы) */}
            <Menu.Item onClick={() => handleOpenReportModal(record)}>
              Отчет
            </Menu.Item>
            
            {(authState.user?.role === UserRole.Admin ||
              authState.user?.role === UserRole.Cashier ||
              authState.user?.role === UserRole.Accountant ||
              authState.user?.role === "director") && (
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${record.workerId}/salary/${record.workerName}/USD&TJS/transactions`
                    )
                  }
                >
                  Смотреть транзакции
                </Menu.Item>
              )}
            <Menu.Item onClick={() => handleSalaryHistoryClick(record)}>
              <Space>
                <HistoryOutlined />
                История зарплат
              </Space>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  // --- Функция конвертации суммы транзакции в валюту отчёта ---
  function convertToReportCurrency(amount: number, txCurrency: string, reportCurrency: string, rate: number) {
    if (txCurrency === reportCurrency) return amount;
    if (txCurrency === 'USD' && reportCurrency === 'TJS') return amount * rate;
    if (txCurrency === 'TJS' && reportCurrency === 'USD') return amount / rate;
    return amount; // fallback
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Зарплата</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          allowClear
          placeholder="Фильтр по валюте"
          onChange={(value) => setCurrency(value)}
          style={{ width: 150 }}
        >
          {Object.values(Currency).map((currency) => (
            <Select.Option key={currency} value={currency}>
              {currency}
            </Select.Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder="Фильтр по типу"
          onChange={(value) => setSalaryType(value)}
          style={{ width: 150 }}
        >
          {Object.values(SalaryType).map((type) => (
            <Select.Option key={type} value={type}>
              {type}
            </Select.Option>
          ))}
        </Select>
        <Checkbox
          checked={hasDebt}
          onChange={(e) => setHasDebt(e.target.checked)}
        >
          Есть долг
        </Checkbox>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={() => downloadFile('/salary/audit-report', 'salary_audit_report.xlsx')}
        >
          Получить отчет ревизии
        </Button>
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={salaries}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Изменить цены"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          {selectedSalary?.pricings.map((pricing) => (
            <div key={pricing._id}>
              <Form.Item name={[pricing._id, "forName"]} label="Название">
                <Input />
              </Form.Item>

              {selectedSalary?.salaryType === SalaryType.PERMONTH ? (
                <Form.Item
                  name={[pricing._id, "perMonth"]}
                  label="Ставка за месяц"
                >
                  <InputNumber min={0} />
                </Form.Item>
              ) : (
                <Form.Item
                  name={[pricing._id, "perUnit"]}
                  label="Ставка за единицу"
                >
                  <InputNumber min={0} />
                </Form.Item>
              )}

              {pricing.for === undefined && (
                <Button onClick={() => handleDeletePricing(pricing._id)}>
                  Удалить
                </Button>
              )}
            </div>
          ))}

          <Button type="primary" onClick={handleAddPricing}>
            Добавить новый прайсинг
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Добавить оплату"
        open={isTransactionOpen}
        onCancel={() => setIsTransactionOpen(false)}
        onOk={() => transactionForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form
          form={transactionForm}
          onFinish={handleTransactionCreate}
          layout="vertical"
        >
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={loading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Select.Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
          >
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Select.Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Введите сумму" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание." }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Добавить премию"
        open={isBonusOpen}
        onCancel={() => setIsBonusOpen(false)}
        onOk={() => bonusForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form
          form={bonusForm}
          onFinish={handleBonusCreate}
          layout="vertical"
        >
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={cashboxesListLoading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Select.Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
          >
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Select.Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Сумма премии"
            rules={[{ required: true, message: "Введите сумму премии" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание премии"
            rules={[{ required: true, message: "Введите описание премии." }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Цена в день"
        open={isPerDayOpen}
        onCancel={() => setIsPerDayOpen(false)}
        onOk={() => perDayForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={perDayForm} layout="vertical" onFinish={handleEditPerDay}>
          <Form.Item
            name="perDay"
            label="Цена в день"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите цену",
              },
            ]}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить долг и оплату"
        open={isEditDebtPaidOpen}
        onCancel={() => setIsEditDebtPaidOpen(false)}
        onOk={() => editDebtPaidForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={editDebtPaidForm} layout="vertical" onFinish={handleEditDebtPaid}>
          <Form.Item
            name="debt"
            label="Долг"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите долг",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="paid"
            label="Оплачено"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите сумму оплаты",
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно истории зарплат работника */}
      <WorkerSalaryHistory
        workerId={selectedSalary?.workerId || ""}
        workerName={selectedSalary?.workerName || ""}
        workerSurname={selectedSalary?.workerSurname}
        visible={isSalaryHistoryOpen}
        onClose={() => setIsSalaryHistoryOpen(false)}
      />

      <Modal
        title={`Отчет: ${reportWorker?.workerName || ''}${reportDateRange && reportDateRange[0] && reportDateRange[1] ? ' (' + formatPeriod(reportDateRange) + ')' : ''}`}
        open={isReportModalOpen}
        onCancel={handleCloseReportModal}
        footer={null}
        width={1500}
      >
        <div style={{ marginBottom: 16 }}>
          <span>Период: </span>
          <RangePicker
            style={{ marginRight: 8 }}
            format="DD.MM.YY"
            value={reportDateRange && reportDateRange[0] && reportDateRange[1] ? [
              reportDateRange[0] ? dayjs(reportDateRange[0], "YYYY-MM-DD") : null,
              reportDateRange[1] ? dayjs(reportDateRange[1], "YYYY-MM-DD") : null
            ] : null}
            onChange={(dates, dateStrings) => {
              if (dates && dateStrings) {
                setReportDateRange([
                  dates[0]?.format("YYYY-MM-DD") || '',
                  dates[1]?.format("YYYY-MM-DD") || ''
                ]);
              } else {
                setReportDateRange(["", ""]);
              }
            }}
            allowClear={false}
          />
          <Button
            type="primary"
            loading={reportLoading}
            onClick={async () => {
              if (!reportWorker || !reportDateRange?.[0] || !reportDateRange?.[1]) return;
              setReportLoading(true);
              try {
                const startDate = toDDMMYY(reportDateRange[0]);
                const endDate = toDDMMYY(reportDateRange[1]);
                const res = await api.get(`/salary/full-report/${reportWorker.workerId}`, {
                  params: {
                    startDate: startDate,
                    endDate: endDate,
                  },
                });
                setSalaryReports(res.data.reports);
                setSalaryTransactions(res.data.transactions);
              } catch (e) {
                message.error('Ошибка загрузки отчета');
              } finally {
                setReportLoading(false);
              }
            }}
          >
            Показать
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            onClick={() => {
              // Печать: открыть новое окно с разметкой для печати
              const printWindow = window.open('', '_blank');
              if (!printWindow) return;
              const tableHtml = document.getElementById('salary-report-table')?.outerHTML || '';
              const txHtml = document.getElementById('salary-tx-table')?.outerHTML || '';
              const summaryHtml = document.getElementById('salary-print-summary')?.outerHTML || '';
              
              // Расчет итогов для печати
              let totalEarned = 0;
              let printSummaryHtml = '';
              
              if (reportWorker?.salaryType === SalaryType.PERMONTH) {
                // Для зарплаты за месяц
                totalEarned = reportWorker?.pricings?.reduce((acc, pricing) => acc + (pricing.perMonth || 0), 0) || 0;
              } else {
                // Для других типов зарплаты
                totalEarned = salaryReports.reduce((acc, report) => {
                  // Пробуем разные варианты названий поля с суммой
                  const reportSum = report.totalPrice || report.total || report.sum || report.amount || 0;
                  return acc + reportSum;
                }, 0);
              }
              
              // Разделяем транзакции на оплаты и премии
              const payments = salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS);
              const bonuses = salaryTransactions.filter(tx => tx.expenseType === ExpenseType.BONUS);
              
              const totalPayments = payments.reduce((acc, tx) => {
                const txCur = tx.currency || tx.salaryCurrency || 'USD';
                return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
              }, 0);
              
              const totalBonuses = bonuses.reduce((acc, tx) => {
                const txCur = tx.currency || tx.salaryCurrency || 'USD';
                return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
              }, 0);
              
              const totalToPay = totalEarned - totalPayments; // Только оплаты вычитаем из зарплаты
              
              if (reportWorker?.salaryType === SalaryType.PERMONTH) {
                printSummaryHtml = `
                  <div style="margin-top: 16px; font-size: 10px;">
                    <div>Зарплата за месяц: <b>${totalEarned.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Оплаты: <b>${totalPayments.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Премии: <b>${totalBonuses.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                  </div>
                `;
              } else {
                printSummaryHtml = `
                  <div style="margin-top: 16px; font-size: 10px;">
                    <div>Общ заработано: <b>${totalEarned.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Оплаты: <b>${totalPayments.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Премии: <b>${totalBonuses.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                    <div>Надо заплатить: <b>${totalToPay.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency}</b></div>
                  </div>
                `;
              }
              
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Печать отчета</title>
                    <style>
                      body, table, th, td { font-family: Arial, sans-serif; font-size: 10px !important; padding: 0 !important; margin: 0 !important; border: 1px solid #000 !important; }
                      th { background: #f5f5f5; }
                      h3 { margin-top: 16px; font-size: 11px; }
                      .no-print { display: none !important; }
                      #salary-print-summary { display: block !important; }
                    </style>
                  </head>
                  <body>
                    <h2 style="font-size:12px; margin:0 0 2px 0;">Отчет: ${reportWorker?.workerName || ''}</h2>
                    <div style="font-size:10px; margin-bottom:2px;">${formatPeriod(reportDateRange)}</div>
                    ${tableHtml}
                    ${txHtml}
                    ${printSummaryHtml}
                  </body>
                </html>
              `);
              printWindow.document.close();
              printWindow.focus();
              printWindow.print();
            }}
          >
            Печать
          </Button>
        </div>
        {reportLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>Загрузка...</div>
        ) : salaryReports.length === 0 && salaryTransactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>Нет данных за выбранный период</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {/* Если есть отчеты, показываем детальный отчет для всех типов зарплаты */}
            {salaryReports.length > 0 ? (
              <div>
                {/* --- АНТД ТАБЛИЦА ОТЧЕТА --- */}
                <Table
                  id="salary-report-table"
                  dataSource={salaryReports.map((report, idx) => ({ ...report, key: report._id || idx }))}
                  pagination={false}
                  bordered
                  scroll={{ x: true }}
                  summary={pageData => {
                    const totalYuk = pageData.reduce((acc, report) => acc + (report.yuk || 0), 0);
                    const totalYukSum = pageData.reduce((acc, report) => acc + ((report.yuk || 0) * yukPrice), 0);
                    // Итог по транзакциям с учётом конвертации
                    const totalTx = salaryTransactions.reduce((acc, tx) => {
                      const txCur = tx.currency || tx.salaryCurrency || 'USD';
                      return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                    }, 0);
                    // Итоги по деталям
                    const detailTotals = getDetailTotals([...pageData], detailColumnsGrouped, reportWorker);
                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}><b>Итого</b></Table.Summary.Cell>
                          {detailColumnsGrouped.map((col: any, idx: number) => [
                            <Table.Summary.Cell key={`q-${idx}`} index={2 + idx * 3} />,
                            <Table.Summary.Cell key={`p-${idx}`} index={3 + idx * 3} />,
                            <Table.Summary.Cell key={`s-${idx}`} index={4 + idx * 3} />
                          ])}
                          <Table.Summary.Cell index={100}><b>{totalYuk}</b></Table.Summary.Cell>
                          <Table.Summary.Cell index={101}><b>{totalYukSum.toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></Table.Summary.Cell>
                        </Table.Summary.Row>
                        {/* Итоги по деталям (только на экране, не в печати) */}
                        <Table.Summary.Row className="ant-table-summary-detail-totals no-print">
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}><span style={{ fontWeight: 400 }}>Сумма по деталям</span></Table.Summary.Cell>
                          {detailTotals.map((tot, idx) => [
                            <Table.Summary.Cell key={`q-tot-${idx}`} index={2 + idx * 3}><b>{tot.qty}</b></Table.Summary.Cell>,
                            <Table.Summary.Cell key={`p-tot-${idx}`} index={3 + idx * 3}></Table.Summary.Cell>,
                            <Table.Summary.Cell key={`s-tot-${idx}`} index={4 + idx * 3}><b>{tot.sum.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b></Table.Summary.Cell>
                          ])}
                          <Table.Summary.Cell index={100}></Table.Summary.Cell>
                          <Table.Summary.Cell index={101}></Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                  columns={reportColumns}
                />
                {/* --- АНТД ТАБЛИЦА ТРАНЗАКЦИЙ --- */}
                {salaryTransactions.length > 0 && (
                  <div style={{ marginTop: 32 }}>
                    <h3>Транзакции</h3>
                    <Table
                      id="salary-tx-table"
                      dataSource={salaryTransactions.map((tx, idx) => ({ ...tx, key: tx._id || idx }))}
                      pagination={false}
                      bordered
                      columns={[
                        { 
                          title: '№', 
                          key: 'index', 
                          width: 60,
                          render: (_: any, __: any, index: number) => index + 1
                        },
                        { title: 'Дата', dataIndex: 'date', key: 'date', render: (v, r) => v || (r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '') },
                        {
                          title: 'Тип',
                          dataIndex: 'expenseType',
                          key: 'expenseType',
                          render: (v) => v === ExpenseType.BONUS ? 'Премия' : 'Оплата'
                        },
                        {
                          title: 'Сумма',
                          dataIndex: 'amount',
                          key: 'amount',
                          render: (v, r) => {
                            const txCur = r.currency || r.salaryCurrency || 'USD';
                            if (txCur === salaryCurrency) return v;
                            // Показываем и оригинал, и эквивалент
                            const converted = convertToReportCurrency(v, txCur, salaryCurrency, r.exchangeRate || exchangeRate || 1);
                            return `${v} ${txCur} (${converted.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency})`;
                          }
                        },
                        { title: 'Валюта', dataIndex: 'currency', key: 'currency', render: (v, r) => v || r.salaryCurrency || salaryCurrency },
                        { title: 'Описание', dataIndex: 'description', key: 'description' },
                      ]}
                    />
                  </div>
                )}
                {/* Итоги */}
                <div style={{ marginTop: 8 }}>
                  {reportWorker?.salaryType === SalaryType.PERMONTH ? (
                    <>
                      <div>Зарплата за месяц: <b>{(reportWorker?.pricings?.reduce((acc, pricing) => acc + (pricing.perMonth || 0), 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Оплаты: <b>{salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Премии: <b>{salaryTransactions.filter(tx => tx.expenseType === ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                    </>
                  ) : (
                    <>
                      <div>Общ заработано: <b>{salaryReports.reduce((acc, report) => {
                        // Пробуем разные варианты названий поля с суммой
                        const reportSum = report.totalPrice || report.total || report.sum || report.amount || 0;
                        return acc + reportSum;
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Оплаты: <b>{salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Премии: <b>{salaryTransactions.filter(tx => tx.expenseType === ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Надо заплатить: <b>{(
                        salaryReports.reduce((acc, report) => {
                          // Пробуем разные варианты названий поля с суммой
                          const reportSum = report.totalPrice || report.total || report.sum || report.amount || 0;
                          return acc + reportSum;
                        }, 0) -
                        salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS).reduce((acc, tx) => {
                          const txCur = tx.currency || tx.salaryCurrency || 'USD';
                          return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                        }, 0)
                      ).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* Если отчетов нет, но есть транзакции, показываем только транзакции */
              salaryTransactions.length > 0 ? (
                <div>
                  <h3>Транзакции</h3>
                  <Table
                    id="salary-tx-table"
                    dataSource={salaryTransactions.map((tx, idx) => ({ ...tx, key: tx._id || idx }))}
                    pagination={false}
                    bordered
                    columns={[
                      { 
                        title: '№', 
                        key: 'index', 
                        width: 60,
                        render: (_: any, __: any, index: number) => index + 1
                      },
                      { title: 'Дата', dataIndex: 'date', key: 'date', render: (v, r) => v || (r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '') },
                      {
                        title: 'Тип',
                        dataIndex: 'expenseType',
                        key: 'expenseType',
                        render: (v) => v === ExpenseType.BONUS ? 'Премия' : 'Оплата'
                      },
                      {
                        title: 'Сумма',
                        dataIndex: 'amount',
                        key: 'amount',
                        render: (v, r) => {
                          const txCur = r.currency || r.salaryCurrency || 'USD';
                          if (txCur === salaryCurrency) return v;
                          // Показываем и оригинал, и эквивалент
                          const converted = convertToReportCurrency(v, txCur, salaryCurrency, r.exchangeRate || exchangeRate || 1);
                          return `${v} ${txCur} (${converted.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salaryCurrency})`;
                        }
                      },
                      { title: 'Валюта', dataIndex: 'currency', key: 'currency', render: (v, r) => v || r.salaryCurrency || salaryCurrency },
                      { title: 'Описание', dataIndex: 'description', key: 'description' },
                    ]}
                  />
                  {/* Итоги для случаев без отчетов */}
                  {reportWorker?.salaryType === SalaryType.PERMONTH && (
                    <div style={{ marginTop: 8 }}>
                      <div>Зарплата за месяц: <b>{(reportWorker?.pricings?.reduce((acc, pricing) => acc + (pricing.perMonth || 0), 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Оплаты: <b>{salaryTransactions.filter(tx => tx.expenseType !== ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                      <div>Премии: <b>{salaryTransactions.filter(tx => tx.expenseType === ExpenseType.BONUS).reduce((acc, tx) => {
                        const txCur = tx.currency || tx.salaryCurrency || 'USD';
                        return acc + convertToReportCurrency(tx.amount || 0, txCur, salaryCurrency, tx.exchangeRate || exchangeRate || 1);
                      }, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {salaryCurrency}</b></div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>Нет данных за выбранный период</div>
              )
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
